import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { gsap } from 'gsap';
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";

import { formatFileUrl, API_URL } from "../../utils/api";

function extractVideoId(url) {
  if (!url) return null;
  const regex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
}

export function Library() {
  const containerRef = useRef(null);
  const playlistScrollRef = useRef(null);
  const booksScrollRef = useRef(null);
  const magazinesScrollRef = useRef(null);

  const [playlists, setPlaylists] = useState([]);
  const [books, setBooks] = useState([]);
  const [magazines, setMagazines] = useState([]);
  const [loading, setLoading] = useState(true);


  const fetchData = useCallback(async () => {
    try {
      const catRes = await fetch(`${API_URL}/library/categories`);
      if (!catRes.ok) throw new Error("API not ok");
      const categories = await catRes.json();

      const vids = categories.filter(c => c.type === "video");
      const bks = categories.filter(c => c.type === "book");

      setPlaylists(vids);
      setBooks(bks);

      const magCats = categories.filter(c => c.type === "magazine");
      if (magCats.length > 0) {
        const magRes = await fetch(`${API_URL}/library/items`);
        if (magRes.ok) {
          const allItems = await magRes.json();
          const magItems = allItems.filter(item =>
            item.category && item.category.type === "magazine"
          );
          setMagazines(magItems);
        } else {
          setMagazines([]);
        }
      } else {
        setMagazines([]);
      }
      setLoading(false);
    } catch (err) {
      console.error("Backend fetch failed:", err);
      setPlaylists([]);
      setBooks([]);
      setMagazines([]);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();

    gsap.fromTo(containerRef.current,
      { opacity: 0, y: 15 },
      { opacity: 1, y: 0, duration: 0.3, ease: "power2.out" }
    );
  }, [fetchData]);

  const scroll = (ref, direction) => {
    if (ref.current) {
      const { scrollLeft, clientWidth } = ref.current;
      const scrollTo = direction === 'left' ? scrollLeft - clientWidth : scrollLeft + clientWidth;
      ref.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
    }
  };

  const fallbackImage = "https://t3.ftcdn.net/jpg/02/48/42/64/360_F_248426448_NVKLywWqArG2ADUxDq6QprtIzsF82dMF.jpg";

  // Removed blocking loader to immediately render page natively and quietly sync in the background

  return (
    <div ref={containerRef} className="w-full min-h-screen bg-white pb-20 font-libre">

      {/* ====================== */}
      {/*     PLAYLISTS SECTION  */}
      {/* ====================== */}
      <div id="videos-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-black text-gray-900 font-AndoraModernSerif tracking-tight">
            Popular Playlist
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={() => scroll(playlistScrollRef, 'left')}
              className="p-3 bg-[#fafafa] hover:bg-white hover:text-pumpkinToast-600 border border-warmGray-100 hover:border-warmGray-200 rounded-2xl shadow-sm transition-all text-gray-400 active:scale-90"
            >
              <IconChevronLeft size={20} />
            </button>
            <button
              onClick={() => scroll(playlistScrollRef, 'right')}
              className="p-3 bg-[#fafafa] hover:bg-white hover:text-pumpkinToast-600 border border-warmGray-100 hover:border-warmGray-200 rounded-2xl shadow-sm transition-all text-gray-400 active:scale-90"
            >
              <IconChevronRight size={20} />
            </button>
          </div>
        </div>

        <div
          ref={playlistScrollRef}
          className="flex overflow-x-auto pb-6 -mx-4 px-4 sm:mx-0 sm:px-0 gap-6 scrollbar-hide scroll-smooth"
        >
          {loading ? Array(4).fill(0).map((_, i) => (
            <div key={i} className="flex-none w-[280px] group">
              <div className="relative h-44 rounded-[24px] bg-gray-100 animate-pulse"></div>
              <div className="mt-2 px-2">
                <div className="h-5 bg-gray-100 rounded w-3/4 animate-pulse mb-1"></div>
                <div className="h-3 bg-gray-100 rounded w-1/2 animate-pulse"></div>
              </div>
            </div>
          )) : playlists.filter(Boolean).map((item) => {
            const videoId = extractVideoId(item.mainUrl || item.videoUrl || "");
            const thumbnail = item.coverImage
              ? formatFileUrl(item.coverImage)
              : (videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : fallbackImage);

            return (
              <Link
                key={item._id || item.id}
                to={item.link ? (item.link.startsWith('/') ? item.link : `/library/${item._id || item.link}`) : `/library/${item._id || item.id}`}
                className="flex-none w-[280px] group transition-all duration-300"
              >
                <div
                  className="relative h-44 rounded-[24px] shadow-sm overflow-hidden bg-gray-100 border border-warmGray-200 group-hover:border-pumpkinToast-200 transition-colors"
                >
                  <img
                    src={thumbnail}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                    onError={(e) => {
                      if (videoId && !e.currentTarget.src.includes('mqdefault')) {
                        e.currentTarget.src = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
                      } else {
                        e.currentTarget.src = fallbackImage;
                      }
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-40 group-hover:opacity-20 transition-opacity"></div>

                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-12 h-12 bg-white text-black rounded-full flex items-center justify-center shadow-2xl scale-90 group-hover:scale-100 transition-transform">
                      <svg className="w-6 h-6 ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>

                  <div className="absolute bottom-3 right-3 bg-white/10 backdrop-blur-md text-white text-[10px] px-3 py-1 rounded-full font-black uppercase tracking-widest border border-white/20">
                    {"Watch Series"}
                  </div>
                </div>
                <div className="mt-2 px-2">
                  <h3 className="font-bold text-base text-gray-800 group-hover:text-pumpkinToast-600 transition-colors leading-tight line-clamp-2">
                    {item.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                      Latest: {item.subtitle || "Episode 01"}
                    </p>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      </div>

      {/* ====================== */}
      {/*     BOOKS SECTION      */}
      {/* ====================== */}
      <div id="books-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-black text-gray-900 font-AndoraModernSerif tracking-tight">
            Popular Books
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={() => scroll(booksScrollRef, 'left')}
              className="p-3 bg-[#fafafa] hover:bg-white hover:text-pumpkinToast-600 border border-warmGray-100 hover:border-warmGray-200 rounded-2xl shadow-sm transition-all text-gray-400 active:scale-90"
            >
              <IconChevronLeft size={20} />
            </button>
            <button
              onClick={() => scroll(booksScrollRef, 'right')}
              className="p-3 bg-[#fafafa] hover:bg-white hover:text-pumpkinToast-600 border border-warmGray-100 hover:border-warmGray-200 rounded-2xl shadow-sm transition-all text-gray-400 active:scale-90"
            >
              <IconChevronRight size={20} />
            </button>
          </div>
        </div>

        <div
          ref={booksScrollRef}
          className="flex overflow-x-auto pb-8 -mx-4 px-4 sm:mx-0 sm:px-0 gap-8 scrollbar-hide scroll-smooth"
        >
          {loading ? Array(5).fill(0).map((_, i) => (
            <div key={i} className="flex-none w-[170px] group">
              <div className="relative aspect-[3/4.2] rounded-[24px] bg-gray-100 animate-pulse"></div>
            </div>
          )) : books.filter(Boolean).map((item) => {
            const targetUrl = item.link
              ? (item.link.startsWith('http') || item.link.startsWith('/') ? item.link : `/books/${item.link}`)
              : `/books/${item._id || item.id}`;

            return (
              <Link
                key={item._id || item.id}
                to={targetUrl}
                className="flex-none w-[170px] group transition-all duration-300"
              >
                <div className="relative aspect-[3/4.2] rounded-[24px] shadow-sm overflow-hidden bg-gradient-to-br from-white to-neutral-50 border border-neutral-200 group-hover:border-pumpkinToast-400 group-hover:shadow-xl transition-all duration-300">
                  <img
                    src={formatFileUrl(item.coverImage || item.bg)}
                    alt={item.title || "Book Category"}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.src = fallbackImage; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-70 group-hover:opacity-40 transition-opacity"></div>

                  <div className="absolute bottom-0 left-0 w-full p-5 flex flex-col items-center text-center bg-gradient-to-t from-black/80 to-transparent">
                    <h3 className="text-white font-bold text-lg leading-tight">
                      {item.title}
                    </h3>
                    <p className="text-pumpkinToast-400 text-[10px] mt-1.5 font-black tracking-widest uppercase">
                      {"View Collection"}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* ====================== */}
      {/*     MAGAZINES SECTION  */}
      {/* ====================== */}
      <div id="magazines-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-black text-gray-900 font-AndoraModernSerif tracking-tight">
            Magazines
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={() => scroll(magazinesScrollRef, 'left')}
              className="p-3 bg-[#fafafa] hover:bg-white hover:text-pumpkinToast-600 border border-warmGray-100 hover:border-warmGray-200 rounded-2xl shadow-sm transition-all text-gray-400 active:scale-90"
            >
              <IconChevronLeft size={20} />
            </button>
            <button
              onClick={() => scroll(magazinesScrollRef, 'right')}
              className="p-3 bg-[#fafafa] hover:bg-white hover:text-pumpkinToast-600 border border-warmGray-100 hover:border-warmGray-200 rounded-2xl shadow-sm transition-all text-gray-400 active:scale-90"
            >
              <IconChevronRight size={20} />
            </button>
          </div>
        </div>

        <div
          ref={magazinesScrollRef}
          className="flex overflow-x-auto pb-10 -mx-4 px-4 sm:mx-0 sm:px-0 gap-8 scrollbar-hide scroll-smooth"
        >
          {loading ? Array(5).fill(0).map((_, i) => (
            <div key={i} className="flex-none w-[180px] group">
              <div className="relative aspect-[1/1.4] rounded-[35px] bg-gray-100 animate-pulse"></div>
            </div>
          )) : magazines.filter(Boolean).map((item) => (
            <a
              key={item._id || item.id}
              href={formatFileUrl(item.pdf)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-none w-[180px] group transition-all duration-300"
            >
              <div className="relative aspect-[1/1.4] rounded-[35px] shadow-2xl overflow-hidden bg-gray-50 border-8 border-white group-hover:border-neutral-100 transition-all">
                <img
                  src={formatFileUrl(item.coverImage || item.img)}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.src = fallbackImage; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent opacity-60 group-hover:opacity-40 transition-opacity"></div>

                <div className="absolute top-4 left-4">
                  <span className="bg-black text-white text-[8px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest backdrop-blur-md shadow-2xl">
                    Edition
                  </span>
                </div>

                <div className="absolute bottom-0 left-0 w-full p-5">
                  <h3 className="text-white font-bold text-base leading-tight font-AndoraModernSerif">
                    {item.title}
                  </h3>
                  <p className="text-neutral-400 text-[10px] mt-1 font-black uppercase tracking-widest">{item.month}</p>
                </div>
              </div>
            </a >
          ))}
        </div>
      </div>
    </div>
  );
}

